export interface GenerationProgress {
  current: number;
  total: number;
  phaseId?: string;
  delivered?: number;
  failed?: number;
}

export interface GenerationProgressEvent {
  phaseId?: string;
  conceptIndex?: number;
  totalConcepts?: number;
  completedConcepts?: number;
  deliveredConcepts?: number;
  failedConcepts?: number;
}

/** Slot identity is not batch completion: concurrent slots finish out of order. */
export function mergeGenerationProgress(previous: GenerationProgress | null, event: GenerationProgressEvent): GenerationProgress | null {
  if (!Number.isSafeInteger(event.totalConcepts) || event.totalConcepts! <= 0) return previous;
  const total = event.totalConcepts!;
  const prior = previous?.total === total && previous.phaseId === event.phaseId ? previous : null;
  const count = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value) ? Math.max(0, Math.min(total, value)) : 0;
  if (event.deliveredConcepts == null || event.failedConcepts == null) {
    // A replay from an older emitter must not erase authoritative delivery counts.
    if (prior?.delivered != null) return prior;
    return {current: count(event.conceptIndex), total, phaseId: event.phaseId};
  }
  const delivered = Math.max(prior?.delivered ?? 0, count(event.deliveredConcepts));
  const failed = Math.min(total - delivered, Math.max(prior?.failed ?? 0, count(event.failedConcepts)));
  return {current: delivered + failed, total, phaseId: event.phaseId, delivered, failed};
}

export function generationProgressLabel(progress: GenerationProgress): string {
  if (progress.delivered == null) return `Generating ${progress.total} images…`;
  return `${progress.delivered} of ${progress.total} images saved${progress.failed ? ` · ${progress.failed} could not finish` : ''}`;
}
